<template>
  <div :class="['st-autocomplete-input', requiredClass]">
    <label
      v-if="!labelInsideInput"
      :id="id"
      :class="'st-autocomplete-input__label-outside'"
      :for="id + 'AutocompleteCombobox'"
      >{{ label }}</label
    >
    <div
      class="st-autocomplete-input__input"
      :class="{
        'st-autocomplete-input__input--has-focus': hasFocus,
        'st-autocomplete-input__input--label-inside': labelInsideInput,
      }"
      role="combobox"
      :aria-expanded="showSuggestions ? 'true' : 'false'"
      :aria-owns="id + 'AutocompleteListbox'"
      aria-haspopup="listbox"
      :aria-labelledby="id"
      :aria-controls="id + 'AutocompleteListbox'"
    >
      <!-- TODO: move label outside of combobox? -->
      <label
        v-if="labelInsideInput"
        class="st-autocomplete-input__input__label"
        :id="id"
        :for="id + 'AutocompleteCombobox'"
        >{{ label }}</label
      >
      <st-input
        aria-autocomplete="list"
        :aria-activedescendant="activedescendant"
        :placeholder="placeholder"
        :id="id + 'AutocompleteCombobox'"
        @input="onInput"
        @keydown.up="onArrowKey('up')"
        @keydown.down="onArrowKey('down')"
        @keydownUp="onArrowKey('up')"
        @keydownDown="onArrowKey('down')"
        :keydown-enter="onEnter"
        :value="value"
        :focus="onFocus"
        :blur="onBlur"
        :tabindex="'0'"
        :required="false"
        :invalid="false"
        :keyup-esc="onEsc"
      />

      <!-- TODO: make own component of the button? -->
      <div
        v-if="showRemoveButton"
        class="st-autocomplete-input__input__remove"
        role="button"
        aria-label="Töm inputfältet."
        @click="onEsc"
      >
        <component :is="icon" size="sm" name="ic-times" :alt="alt" useSvg />
      </div>
    </div>
    <ul
      class="st-autocomplete-input__suggestions"
      :id="id + 'AutocompleteListbox'"
      role="listbox"
      :aria-labelledby="id"
      v-show="suggestionsVisible"
      @mousedown.prevent
    >
      <li
        v-for="(item, index) in autocompleteSuggestions"
        @click="click(item)"
        :key="item.id"
        role="option"
        :id="id + 'Option-' + index"
        :aria-selected="
          selectedItem.displayName === item.displayName || 'false'
        "
        :class="
          index === focusIndex
            ? 'st-autocomplete-input__suggestions__item st-autocomplete-input__suggestions__item--focused'
            : 'st-autocomplete-input__suggestions__item'
        "
      >
        {{ item.displayName }}
        <div
          class="st-autocomplete-input__suggestions__item__extra-info"
          v-if="item.extraInfo"
        >
          {{ item.extraInfo }}
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import StInput from "../input/Input.vue";
import StIcon from "../icon/Icon.vue";
import StIconLegacy from "../icon/IconLegacy.vue";
import { version } from "vue";

export default {
  name: "st-autocomplete-input",
  components: {
    StInput,
    StIcon,
    StIconLegacy,
  },
  data() {
    return {
      focusIndex: 0,
      value: "",
      showSuggestions: false,
      selectedItem: {},
      selectionByClick: false,
      selectionByEnter: false,
      hasFocus: false,
    };
  },
  computed: {
    icon() {
      return version && parseInt(version) === 3 ? "StIcon" : "StIconLegacy";
    },
    focusedItem() {
      return this.autocompleteSuggestions[this.focusIndex];
    },
    activedescendant() {
      return this.id + "Option-" + this.focusIndex;
    },
    suggestionsVisible() {
      return this.showSuggestions && this.autocompleteSuggestions.length > 0;
    },
    requiredClass: function() {
      return this.required && "required";
    },
  },
  props: {
    label: {
      type: String,
      default: "",
    },
    labelInsideInput: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    autocompleteSuggestions: {
      default: [
        {
          displayName: "",
          extraInfo: "",
          id: "",
        },
      ],
    },
    overrideValue: {
      type: String,
      default: "",
    },
    showRemoveButton: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    overrideValue: function(newVal) {
      this.value = newVal;
      this.$nextTick(() => (this.showSuggestions = false));
    },
    suggestionsVisible: function(bool) {
      this.$emit("suggestionsVisible", bool);
    },
  },
  methods: {
    onInput(val) {
      this.focusIndex = 0;
      this.showSuggestions = true;
      this.value = val;
      this.$emit("input", val);
    },
    onFocus() {
      this.showSuggestions = true;
      this.selectionByClick = false;
      this.selectionByEnter = false;
      this.$emit("input", this.value);
      this.focusIndex = 0;
      this.hasFocus = true;
    },
    onArrowKey(direction) {
      if (direction === "up") {
        if (this.focusIndex > 0) this.focusIndex--;
        else this.focusIndex = this.autocompleteSuggestions.length - 1;
      } else if (direction === "down") {
        if (this.focusIndex >= this.autocompleteSuggestions.length - 1)
          this.focusIndex = 0;
        else this.focusIndex++;
      }
    },
    onBlur() {
      this.showSuggestions = false;
      if (!this.selectionByClick && !this.selectionByEnter) this.setItem();
      this.hasFocus = false;
    },
    onEnter() {
      this.selectionByEnter = true;
      this.setItem();
    },
    setItem() {
      if (this.focusedItem) {
        this.selectedItem = this.focusedItem;
        this.select();
      }
    },
    onEsc() {
      this.value = "";
      this.$emit("esc");
    },
    click(item) {
      this.selectedItem = item;
      this.selectionByClick = true;
      this.select();
    },
    select() {
      this.value = this.selectedItem.displayName;
      this.$nextTick(() => (this.showSuggestions = false));
      this.$emit("select", this.selectedItem);
    },
  },
};
</script>

<style lang="scss">
@import "../../scss/variables.scss";
@import "../../scss/colors.scss";
@import "../../scss/functions.scss";
@import "../../scss/fonts.scss";

.st-autocomplete-input {
  width: 100%;
  position: relative;

  &.required {
    label:after {
      content: " - obligatoriskt";
      font-style: italic;
      font-weight: 400;
    }
  }

  &__label-outside {
    font-size: rem-calc(14);
    font-weight: 600;
    font-family: $source-sans-pro;
    color: $dark-grey;
    line-height: 1.4;
    display: inline-block;
    margin-bottom: rem-calc(4);
    text-align: left;
  }

  &__input {
    display: flex;
    align-items: center;
    border: $border-width--thin solid $dark-grey;
    border-radius: $border-radius;
    &__label {
      width: rem-calc(48);
      margin-bottom: 0;
      display: flex;
      justify-content: center;
      color: $dark-grey;
    }
    &__remove {
      margin-right: $xs;
      padding: $xs;
      cursor: pointer;
      border-radius: 50%;
      &:hover {
        background-color: $light-grey;
      }
    }
    .st-input__input-field,
    .st-input__input-field:focus {
      border: none;
    }
    &--label-inside {
      .st-input__input-field,
      .st-input__input-field:focus {
        border-left: $border-width--thin solid $light-grey;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
    &--has-focus {
      border-width: $border-width;
    }
  }
  &__suggestions {
    list-style: none;
    position: absolute;
    background-color: $white;
    width: inherit;
    z-index: 3;
    margin: 0;
    padding: 0;
    border: $border-width--thin solid $dark-grey;
    border-bottom-left-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
    border-top: none;
    &__item {
      font-weight: $semi-bold;
      padding: $md;
      &:not(:last-child) {
        border-bottom: $border-width--thin solid $light-grey;
      }
      &--focused,
      &:hover {
        background-color: $light-grey;
        cursor: pointer;
      }
      &__extra-info {
        font-size: rem-calc(14);
        font-weight: $regular;
      }
    }
  }
}
</style>
